import { MouseEvent, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { NavLink, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { Box, Button, Toolbar, Menu, Avatar, Typography, IconButton, AppBar, Badge, createTheme, ThemeProvider } from "@mui/material";
import { Person as PersonIcon, Logout as LogoutIcon, Article as ArticleIcon, Home as HomeIcon, Tune as TuneIcon, BarChart as BarChartIcon } from '@mui/icons-material';
import classes from "./Navigation.module.scss";
import SearchBar from "../search/SearchBar";
import { RouteNames } from "../../enums/RouteNames";
import FiltersDrawer from "../filters-drawer/FiltersDrawer";
import { Filters } from '../../interfaces/Filters';
import { RootState } from "../../../store";

const theme = createTheme({
  palette: {
    success: {
      main: '#00A767',
    },
  },
});

const Navigation = () => {
  const navigate = useNavigate();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElFilters, setAnchorElFitlers] = useState<boolean>(false);

  const filters: Filters = useSelector(
    (state: RootState) => state.filters.filters
  );

  const countActiveFilters = useMemo(() => {
    let totalCount = 0;

    for (const key in filters) {
      const filterValue = filters[key as keyof Filters];

      if (null !== filterValue) {
        if (Array.isArray(filterValue)) {
          totalCount += filterValue.length;
        } else {
          totalCount += 1;
        }
      }
    }

    return totalCount;
  }, [filters]);



  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logoutHandler = () => {
    secureLocalStorage.clear();
    navigate(RouteNames.LOGIN);
  };

  const handleOpenFilters = (event: MouseEvent<HTMLElement>) => {
    setAnchorElFitlers(true);
  };

  const handleCloseFilters = () => {
    setAnchorElFitlers(false);
  };


  return (
    <>
      <AppBar position='static' className={`${classes.appbar} py-2`}>
        <Toolbar classes={{ root: `d-flex justify-content-between py-4 flex-wrap` }}>
          <NavLink
            to="/"
            className={({ isActive }) =>
              (isActive ? 'active' : 'inactive') + 'mr-3'
            }
          >
            <Box className='logo order-1' />
          </NavLink>
          <Box className={`${classes.search}`}>
            <SearchBar />
          </Box>
          <Box className='order-2'>
            <Box className='d-flex flex-row'>
              <IconButton onClick={handleOpenUserMenu}>
                <Avatar className={classes.avatar} />
              </IconButton>
              <IconButton className={classes.filtersButton} onClick={handleOpenFilters}>
                <ThemeProvider theme={theme}>
                  <Badge badgeContent={countActiveFilters} color='success' max={9} >
                    <TuneIcon className={classes.filters} />
                  </Badge>
                </ThemeProvider>
              </IconButton>
            </Box>
            <FiltersDrawer isOpen={anchorElFilters} onClose={handleCloseFilters} />
            <Menu
              classes={{
                paper: 'p-2',
              }}
              anchorEl={anchorElUser}
              keepMounted
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <NavLink
                onClick={handleCloseUserMenu}
                to={RouteNames.HOME}
                className={({ isActive }) =>
                  isActive ? 'active my-2' : 'inactive my-2'
                }
              >
                <Typography className={'d-flex align-items-center'}>
                  <HomeIcon className={`me-2`} />
                  Home
                </Typography>
              </NavLink>
              <NavLink
                onClick={handleCloseUserMenu}
                to={RouteNames.PROFILE}
                className={({ isActive }) =>
                  isActive ? 'active my-2' : 'inactive my-2'
                }
              >
                <Typography className={'d-flex align-items-center'}>
                  <PersonIcon className={`me-2`} />
                  Profile
                </Typography>
              </NavLink>
              <NavLink
                onClick={handleCloseUserMenu}
                to={RouteNames.MY_VARIATIONS}
                className={({ isActive }) =>
                  isActive ? 'active my-2' : 'inactive my-2'
                }
              >
                <Typography className={'d-flex align-items-center'}>
                  <ArticleIcon className='me-2' />
                  Variations
                </Typography>
              </NavLink>
              <NavLink
                onClick={handleCloseUserMenu}
                to={RouteNames.STATISTICS_DASHBOARD}
                className={({ isActive }) =>
                  isActive ? 'active my-2' : 'inactive my-2'
                }
              >
                <Typography className={'d-flex align-items-center'}>
                  <BarChartIcon className='me-2' />
                  Statistics
                </Typography>
              </NavLink>
              <Button
                onClick={logoutHandler}
                className={`${classes.logout} w-100 mt-3`}
                size='medium'
              >
                <LogoutIcon className={'me-2'} />
                Log out
              </Button>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Navigation;
